import { Session } from "@supabase/supabase-js";

import { SupaUser } from "types";

import { isMfaEnrollmentRequiredForUser } from "./enforcement";

export interface MfaState {
  // A verified TOTP factor exists but the session is still aal1 — the user must
  // pass the 6-digit challenge before the app guard lets them in.
  isChallengeRequired: boolean;
  // No verified factor yet and the enforcement predicate applies — the user must
  // enroll an authenticator before the app guard lets them in.
  isEnrollmentRequired: boolean;
}

export const initialMfaState: MfaState = {
  isChallengeRequired: false,
  isEnrollmentRequired: false
};

type AuthenticatorAssuranceLevel = "aal1" | "aal2";

// Derived synchronously from the JWT instead of the async
// supabase.auth.mfa.getAuthenticatorAssuranceLevel() so that the MFA flags are always
// set in the same state update as `user` — otherwise the route guards would briefly
// admit an aal1 session while the async call resolves.
export const getSessionAal = (session: Session): AuthenticatorAssuranceLevel => {
  try {
    const payload = session.access_token.split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));

    return decoded.aal === "aal2" ? "aal2" : "aal1";
  } catch {
    return "aal1";
  }
};

export const hasVerifiedTotpFactor = (user: SupaUser): boolean =>
  (user.factors ?? []).some((f) => f.factor_type === "totp" && f.status === "verified");

export const deriveMfaState = (session: Session | null | undefined): MfaState => {
  const user = session?.user as SupaUser | undefined;

  if (!session || !user) {
    return initialMfaState;
  }

  // SSO users authenticate (incl. MFA) through their identity provider.
  const isSSOUser = !!user.app_metadata.provider?.startsWith("sso:");
  const hasFactor = hasVerifiedTotpFactor(user);

  return {
    isChallengeRequired: hasFactor && getSessionAal(session) === "aal1",
    isEnrollmentRequired: !hasFactor && !isSSOUser && isMfaEnrollmentRequiredForUser(user)
  };
};
